import { useState } from 'react'

import type { CustomFieldSchema } from '@tribeplatform/gql-client/types'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { useAdminPanel } from '../../AdminPanel/providers/AdminPanelProvider.js'
import { T } from '../../i18n/components/T.js'
import { useStaticIntl } from '../../i18n/hooks/useStaticIntl.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { GenericSettings } from '../../Views/components/GenericSettings.js'
import { FieldSettings } from '../settings/FieldSettings.js'
import { ArrayOptions, type CMSSharedState } from '../types.js'
import {
  multipleFieldOptionsFactory,
  singleFieldOptionsFactory,
} from '../utils/fieldOptions.js'
import {
  defaultInitialSingleField,
  defaultInitialMultipleField,
} from '../utils/initialFieldSchemas.js'
import { arrayOptionsFactory } from '../utils/selectOptions.js'
import { isFieldLocked } from '../utils/utils.js'

import { FieldOption } from './FieldOption.js'

interface Props {
  postTypeId: string
  goBack: () => void
}
export const AddNewField = ({ postTypeId, goBack }: Props) => {
  const { $t } = useI18n()
  const [showFieldSettings, setShowFieldSettings] = useState(false)
  const [arrayOption, setArrayOption] = useState<ArrayOptions>(
    ArrayOptions.single,
  )
  const [selectedFieldOption, setSelectedFieldOption] =
    useState<CustomFieldSchema>(defaultInitialSingleField)
  const { setSharedState } = useAdminPanel<CMSSharedState>()
  const singleFieldOptions = useStaticIntl(singleFieldOptionsFactory)
  const multipleFieldOptions = useStaticIntl(multipleFieldOptionsFactory)
  const arrayOptions = useStaticIntl(arrayOptionsFactory)

  return (
    <>
      <GenericSettings
        title={$t({
          defaultMessage: 'Define data fields',
          id: 'Admin.CMSFields.DefineDataFields',
        })}
        description={
          <T
            defaultMessage="Select a field type to capture the info you need"
            id="Admin.CMSFields.AddNewFieldHelperText"
          />
        }
        goBack={goBack}
      >
        <div className="space-y-5">
          <FormControl.ButtonGroupSelect
            name="arrayOptions"
            fullWidth
            value={arrayOption}
            onChange={(arrayOption: ArrayOptions) => {
              setArrayOption(arrayOption)
              if (arrayOption === ArrayOptions.multiple) {
                setSelectedFieldOption(defaultInitialMultipleField)
                setSharedState({ newField: defaultInitialMultipleField })
                return
              }

              if (arrayOption === ArrayOptions.single) {
                setSelectedFieldOption(defaultInitialSingleField)
                setSharedState({
                  newField: defaultInitialSingleField,
                })
              }
            }}
            items={arrayOptions}
          />
          <div className="grid grid-cols-3 gap-2">
            {arrayOption === ArrayOptions.single && (
              <>
                {singleFieldOptions.map(
                  ({ id, name, iconName, initialField }) => (
                    <FieldOption
                      key={id}
                      name={name}
                      iconName={iconName}
                      active={selectedFieldOption.key === id}
                      onClick={() => {
                        setSelectedFieldOption(initialField)
                        setSharedState({ newField: initialField })
                      }}
                    />
                  ),
                )}
              </>
            )}
            {arrayOption === ArrayOptions.multiple && (
              <>
                {multipleFieldOptions.map(
                  ({ id, name, iconName, initialField }) => (
                    <FieldOption
                      key={id}
                      name={name}
                      iconName={iconName}
                      active={selectedFieldOption.key === id}
                      onClick={() => {
                        setSelectedFieldOption(initialField)
                        setSharedState({ newField: initialField })
                      }}
                    />
                  ),
                )}
              </>
            )}
          </div>
          <Button
            variant="secondaryNeutral"
            onClick={() => {
              setShowFieldSettings(true)
            }}
            fullWidth
            trailingIcon={<SvgIcon name="arrow-right" />}
          >
            <T defaultMessage="Next" id="Generics.Next" />
          </Button>
        </div>
      </GenericSettings>
      {showFieldSettings && (
        <FieldSettings
          field={selectedFieldOption}
          setField={newField => {
            setSharedState({ newField })
            setSelectedFieldOption(newField)
          }}
          postTypeId={postTypeId}
          isLocked={isFieldLocked(selectedFieldOption?.key)}
          isNew
          goBack={() => setShowFieldSettings(false)}
          goToFields={() => {
            setShowFieldSettings(false)
            goBack()
          }}
        />
      )}
    </>
  )
}
